/**
 * Brand Context
 * Manages currently selected brand across the application
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { Brand } from '../types';

interface BrandContextType {
  selectedBrand: Brand | null;
  setSelectedBrand: (brand: Brand | null) => void;
}

const BrandContext = createContext<BrandContextType | undefined>(undefined);

export function BrandProvider({ children }: { children: ReactNode }) {
  const [selectedBrand, setSelectedBrandState] = useState<Brand | null>(null);

  // Load saved brand from localStorage on mount
  useEffect(() => {
    const savedBrandId = localStorage.getItem('selected_brand_id');
    if (savedBrandId) {
      // Will be populated when brands are loaded
      console.log('Saved brand ID:', savedBrandId);
    }
  }, []);

  const setSelectedBrand = (brand: Brand | null) => {
    setSelectedBrandState(brand);
    if (brand) {
      localStorage.setItem('selected_brand_id', brand.id);
    } else {
      localStorage.removeItem('selected_brand_id');
    }
  };

  const value: BrandContextType = {
    selectedBrand,
    setSelectedBrand,
  };

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}

export function useBrand() {
  const context = useContext(BrandContext);
  if (context === undefined) {
    throw new Error('useBrand must be used within a BrandProvider');
  }
  return context;
}
